import { FunctionComponent, useCallback, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import { getAureusPurpose, triggerAureusDeboosting } from '@utils/aureus'
import { executeQuery as executePolarisPopularQuery } from '@utils/aureus/queries/polarisPopular'
import useHasAureusJsClientScriptInitialized from '@hooks/useHasAureusJsClientScriptInitialized'
import usePageMetadata from '@hooks/usePageMetadata'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import { JSONWidgetType as TeaserStandardVerticalJSONWidgetType } from '@widgets/TeaserStandardVertical'
import { ToplistAPIProps } from '@widgets/Toplist'
import Wrapper from '@components/Toplist/Wrapper'

import Story from './Toplist/Story'

type MostReadProps = Pick<ToplistAPIProps, 'title' | 'hideOnDesktop'>

type MostReadItem = TeaserStandardVerticalJSONWidgetType & {
  articleId: string
  aureusOfferId: string
  order: number
}

const getMostRead = async (id: string) => {
  const queryResult =
    (await executePolarisPopularQuery({
      contentBlacklist: id,
      purpose: getAureusPurpose(),
    })) ?? []

  return queryResult.reduce((acc, curr, index) => {
    acc.push({
      ...curr.teasers.standardVertical,
      articleId: curr.articleID,
      aureusOfferId: curr.aureusOfferId,
      order: index + 1,
    } satisfies MostReadItem)

    return acc
  }, [] as MostReadItem[])
}

const MostReadStory: FunctionComponent<MostReadItem> = ({
  articleId,
  title,
  catchword,
  link,
  isPlus,
  aureusOfferId,
  order,
}) => {
  const onClick = useCallback(
    () => ({
      event: 'select_content',
      placement: 'article-recommendations-most-read',
      link_id: link?.href,
    }),
    [link?.href],
  )

  const trackOnClick = useTracking(onClick)

  const onClickWithDeboosting = useCallback(() => {
    triggerAureusDeboosting({
      itemsToDeboost: [{ offerId: aureusOfferId, contentId: articleId }],
    })

    trackOnClick()
  }, [articleId, aureusOfferId, trackOnClick])

  return (
    <Story
      key={articleId}
      order={order}
      title={title}
      catchword={catchword}
      articleId={articleId}
      link={link}
      isPlus={isPlus}
      aureusOfferId={aureusOfferId}
      onClick={onClickWithDeboosting}
    />
  )
}

const MostRead: FunctionComponent<MostReadProps> = ({
  title,
  hideOnDesktop,
}) => {
  const pageMetadata = usePageMetadata()

  const id = pageMetadata.id

  const hasAureusJsClientScriptInitialized =
    useHasAureusJsClientScriptInitialized()

  const { data: mostReadItems } = useQuery<MostReadItem[]>({
    queryKey: ['most-read', id],
    queryFn: () => getMostRead(id as string),
    enabled: !!id && hasAureusJsClientScriptInitialized,
    gcTime: 0,
  })

  const renderedMostReadItems = useMemo(
    () =>
      mostReadItems ? (
        <>
          {mostReadItems.map((mostReadItem) => (
            <MostReadStory key={mostReadItem.articleId} {...mostReadItem} />
          ))}
        </>
      ) : null,
    [mostReadItems],
  )

  const onImpressionTracking = useCallback<TrackingFnType>(
    () => ({
      event: 'content_impression',
      placement: 'article-recommendations-most-read',
      link_id: pageMetadata.url,
    }),
    [pageMetadata.url],
  )

  const onImpression = useTracking(onImpressionTracking)

  return (
    <Wrapper
      title={title}
      hideOnDesktop={hideOnDesktop}
      onImpression={onImpression}
    >
      {renderedMostReadItems}
    </Wrapper>
  )
}

export default MostRead
